"use client"

import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toggleSidebarState, closeMobileSidebar, setDropped } from "../../redux/sidebarSlice";
import Menu from "./Menu";
import UserProfileCard from "../UserProfileCard";
import NotificationBell from "../NotificationBell";
import gsap from 'gsap';

// Redux selectors
import { selectSidebarIsClose } from "../../redux/sidebarSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import { selectUserData } from "../../redux/main/userSlice";
import { selectUser, getLogout } from "../../redux/main/authSlice";
import { selectColorsSchema } from "../../redux/main/uiSlice";
import { selectIsTestEnv } from "../../redux/main/accountSlice";

export default function Sidebar({ isMobile }) {
    // Gestisco apertura e chiusura della sidebar
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isClose = useSelector(selectSidebarIsClose);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    
    const handleClosingSidebar = () => {
        dispatch(setDropped(null));
        dispatch(toggleSidebarState());
    }
    
    const handleLogout = () => {
        setShowLogoutModal(true);
    }
    
    const confirmLogout = () => {
        dispatch(getLogout());
        navigate("/login");
        setShowLogoutModal(false);
    }
    
    const cancelLogout = () => {
        setShowLogoutModal(false);
    }

    const accountData = useSelector(selectAccountData);
    const userData = useSelector(selectUserData);
    const colorsSchema = useSelector(selectColorsSchema);
    const authUser = useSelector(selectUser);
    const isTestEnv = useSelector(selectIsTestEnv);
    
    // Verifica se l'utente è di tipo customer (contatto cliente)
    const isCustomerUser = authUser && authUser.customerId;
    
    // Determina il nome e cognome dell'utente da visualizzare
    // Usa userData se disponibile (caricato da getUserDetails), altrimenti fallback ad authUser (da localStorage)
    // Questo vale sia per customer che per employee
    const userName = userData?.name || authUser?.name || (isCustomerUser ? 'Cliente' : 'Utente');
    const userSurname = userData?.surname || authUser?.surname || '';
    const userRole = isCustomerUser ? 'Cliente' : (userData?.roleLabel || authUser?.roleLabel || 'Utente');

    // Gestisco l'opacità di entrata delle label
    useEffect(() => {
        if (!isClose) {
            gsap.fromTo(".gsap-opacize-me",
                { opacity: 0 },
                {
                    opacity: 1,
                    duration: 1,
                    delay: 0.2
                }
            )
        }

    }, [isClose])
    const handleLinkClick = () => {
        if (isMobile) {
            dispatch(closeMobileSidebar());
        }
    };

    return (
        <div 
            className={`box ${isMobile ? 'w-[280px]' : (!isClose ? 'w-[300px]' : 'w-[80px]')} relative transition-all duration-300 ease-in-out flex flex-col gap-4 ${isMobile ? 'h-screen overflow-y-auto' : 'h-fit'} z-20 ${isTestEnv ? 'bg-yellow-100' : 'bg-white'}`}
        >
            {/* left arrow - nascosto su mobile */}
            {!isMobile && (
                <div className={`absolute right-[-12px] bg-white top-4 p-1 ${isClose ? 'w-6 h-6 text-xs' : 'w-8 h-8'} flex justify-center items-center border-2 border-[#6E6B7D] pt-2 pr-1.5 rounded-full cursor-pointer ${isClose && 'rotate-180'}`} onClick={handleClosingSidebar}><i className="fi fi-br-angle-left"></i></div>
            )}

            {/* Logo Account */}
            <div className="min-h-[50px] w-full flex items-end justify-center">
                <img 
                    src={accountData?.settings?.UI?.logo || '/vite.svg'} 
                    alt="Logo" 
                    className="max-h-[50px]" 
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/vite.svg';
                    }}
                />
            </div>

            {/* Banner ambiente di test */}
            {isTestEnv && (!isClose || isMobile) && (
                <div className="bg-red-600 text-white text-center py-1 px-2 text-xs font-bold uppercase tracking-wider rounded mx-2">
                    AMBIENTE DI TEST
                </div>
            )}
            {isTestEnv && isClose && !isMobile && (
                <div className="bg-red-600 text-white text-center py-1 px-1 text-[8px] font-bold uppercase rounded mx-1">
                    TEST
                </div>
            )}

            {/* User Profile con Notification Bell integrata */}
            {(userData || isCustomerUser) && (
                <UserProfileCard 
                    isClose={isClose} 
                    avatar={isCustomerUser ? null : userData?.avatar} 
                    defaultAvatar={accountData?.settings?.defaultUserAvatar}
                    name={userName} 
                    surname={userSurname} 
                    roleLabel={userRole} 
                    isCustomerUser={isCustomerUser}
                    notificationBell={!isCustomerUser && authUser ? <NotificationBell size="small" /> : null}
                />
            )}

            {/* Menu - Usa sempre Menu.jsx, che filtrerà i moduli in base al tipo di utente */}
            {isCustomerUser ? (
                // Menu personalizzato per i clienti
                <div className="flex flex-col">
                    {/* Dashboard */}
                    <NavLink to="/customerDashboard" onClick={handleLinkClick} className={({isActive}) => 
                        `text-sm w-full py-2 px-4 ${isActive ? 'bg-slate-100' : 'hover:bg-slate-100'} rounded-lg transform-all duration-300 easy-in-out cursor-pointer flex items-center ${isClose && !isMobile ? 'justify-center' : 'justify-between'} min-h-[40px]`
                    }>
                        <div className={`flex gap-2 items-center ${isClose && !isMobile ? 'w-[44px]' : 'w-[30px]'}`}>
                            <i className="fi fi-br-home"></i>
                        </div>
                        {(!isClose || isMobile) && <div className="gsap-opacize-me flex-grow h-[24px] leading-4 flex items-center">
                            Dashboard
                        </div>}
                    </NavLink>
                    
                    {/* I miei impianti */}
                    <NavLink to="/customerSystems" onClick={handleLinkClick} className={({isActive}) => 
                        `text-sm w-full py-2 px-4 ${isActive ? 'bg-slate-100' : 'hover:bg-slate-100'} rounded-lg transform-all duration-300 easy-in-out cursor-pointer flex items-center ${isClose && !isMobile ? 'justify-center' : 'justify-between'} min-h-[40px]`
                    }>
                        <div className={`flex gap-2 items-center ${isClose && !isMobile ? 'w-[44px]' : 'w-[30px]'}`}>
                            <i className="fi fi-br-settings"></i>
                        </div>
                        {(!isClose || isMobile) && <div className="gsap-opacize-me flex-grow h-[24px] leading-4 flex items-center">
                            I miei impianti
                        </div>}
                    </NavLink>
                    
                    {/* Richieste di assistenza */}
                    <NavLink to="/customerAssistanceRequests" onClick={handleLinkClick} className={({isActive}) => 
                        `text-sm w-full py-2 px-4 ${isActive ? 'bg-slate-100' : 'hover:bg-slate-100'} rounded-lg transform-all duration-300 easy-in-out cursor-pointer flex items-center ${isClose && !isMobile ? 'justify-center' : 'justify-between'} min-h-[40px]`
                    }>
                        <div className={`flex gap-2 items-center ${isClose && !isMobile ? 'w-[44px]' : 'w-[30px]'}`}>
                            <i className="fi fi-br-ticket"></i>
                        </div>
                        {(!isClose || isMobile) && <div className="gsap-opacize-me flex-grow h-[24px] leading-4 flex items-center">
                            Richieste di assistenza
                        </div>}
                    </NavLink>
                    
                    {/* I miei pagamenti */}
                    <NavLink to="/customerPayments" onClick={handleLinkClick} className={({isActive}) => 
                        `text-sm w-full py-2 px-4 ${isActive ? 'bg-slate-100' : 'hover:bg-slate-100'} rounded-lg transform-all duration-300 easy-in-out cursor-pointer flex items-center ${isClose && !isMobile ? 'justify-center' : 'justify-between'} min-h-[40px]`
                    }>
                        <div className={`flex gap-2 items-center ${isClose && !isMobile ? 'w-[44px]' : 'w-[30px]'}`}>
                            <i className="fi fi-br-credit-card"></i>
                        </div>
                        {(!isClose || isMobile) && <div className="gsap-opacize-me flex-grow h-[24px] leading-4 flex items-center">
                            I miei pagamenti
                        </div>}
                    </NavLink>
                </div>
            ) : accountData && accountData.settings?.UI?.modules ? (
                <Menu modules={accountData.settings.UI.modules} onLinkClick={handleLinkClick} />
            ) : (
                <div className="px-4 py-2 text-sm text-gray-500">
                    Menu in caricamento...
                </div>
            )}
            
            {/* Logout */}
            <div
                onClick={() => setShowLogoutModal(true)}
                className={`text-sm w-full py-2 px-4 hover:bg-slate-100 rounded-lg transform-all duration-300 easy-in-out cursor-pointer flex items-center ${isClose && !isMobile ? 'justify-center' : 'justify-between'} text-red-600 min-h-[40px]`}
            >
                <div className={`flex gap-2 items-center ${isClose && !isMobile ? 'w-[44px]' : 'w-[30px]'}`}>
                    <i className="fi fi-br-sign-out-alt"></i>
                </div>
                {(!isClose || isMobile) && <div className="gsap-opacize-me flex-grow h-[24px] leading-4 flex items-center">
                    Logout
                </div>}
            </div>
            
            {/* Logout Modal */}
            {showLogoutModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-sm w-full">
                        <h3 className="text-lg font-medium mb-4">Conferma Logout</h3>
                        <p className="mb-6">Sei sicuro di voler effettuare il logout?</p>
                        <div className="flex justify-end gap-3">
                            <button 
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
                                onClick={cancelLogout}
                            >
                                No
                            </button>
                            <button 
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                                onClick={confirmLogout}
                            >
                                Sì
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
